import { useEffect, useState } from 'react';
import type { OrgUnitKind, OrgUnitNode, OrgUnitPayload } from './types';
import { ORG_UNIT_KIND_LABEL } from './types';

const CHILD_KINDS: Record<OrgUnitKind | 'root', OrgUnitKind[]> = {
  root: ['general'],
  general: ['planta'],
  planta: ['sector'],
  sector: ['proceso'],
  proceso: ['puesto'],
  puesto: [],
};

interface OrgUnitFormModalProps {
  open: boolean;
  parentNode: OrgUnitNode | null;
  editing: OrgUnitNode | null;
  onClose: () => void;
  onSubmit: (payload: OrgUnitPayload) => Promise<void>;
}

export function OrgUnitFormModal({
  open,
  parentNode,
  editing,
  onClose,
  onSubmit,
}: OrgUnitFormModalProps) {
  const parentKindForNew: OrgUnitKind | 'root' = parentNode?.kind ?? 'root';
  const allowedKinds: OrgUnitKind[] = editing ? [editing.kind] : CHILD_KINDS[parentKindForNew];

  const [kind, setKind] = useState<OrgUnitKind>(allowedKinds[0] ?? 'general');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(editing?.kind ?? allowedKinds[0] ?? 'general');
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setLocation(editing?.location ?? '');
  }, [open, editing, allowedKinds]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        parentId: editing ? editing.parentId : parentNode?.id ?? null,
        kind,
        name,
        code: code || null,
        location: location || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
      <div className='w-full max-w-md rounded-lg bg-white p-6 shadow-xl'>
        <h2 className='mb-4 text-lg font-semibold'>
          {editing ? 'Editar nodo' : 'Nuevo nodo'}
        </h2>
        <form onSubmit={handleSubmit} className='space-y-4'>
          {!editing && (
            <div>
              <label className='mb-1 block text-sm font-medium'>Tipo</label>
              <select
                className='w-full rounded border px-3 py-2'
                value={kind}
                onChange={e => setKind(e.target.value as OrgUnitKind)}
              >
                {allowedKinds.map(k => (
                  <option key={k} value={k}>
                    {ORG_UNIT_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className='mb-1 block text-sm font-medium'>Nombre</label>
            <input
              className='w-full rounded border px-3 py-2'
              value={name}
              onChange={e => setName(e.target.value)}
              required
              maxLength={200}
              placeholder='Ej: Planta Rosario, Sector Mantenimiento'
            />
          </div>
          <div>
            <label className='mb-1 block text-sm font-medium'>Código</label>
            <input
              className='w-full rounded border px-3 py-2'
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={40}
              placeholder='Ej: PLT-01 (opcional)'
            />
          </div>
          <div>
            <label className='mb-1 block text-sm font-medium'>Ubicación</label>
            <input
              className='w-full rounded border px-3 py-2'
              value={location}
              onChange={e => setLocation(e.target.value)}
              maxLength={200}
              placeholder='Ej: Parque industrial, edificio B'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <button type='button' className='rounded border px-4 py-2' onClick={onClose}>
              Cancelar
            </button>
            <button
              type='submit'
              disabled={saving}
              className='rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50'
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
